import { motion } from "framer-motion";
import { ArrowLeft, Flame, Users, Rocket, Heart, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Manifesto = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-anyskill-purple/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-anyskill-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-anyskill-orange/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-12 md:py-20">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-anyskill-purple/10 rounded-full mb-6">
            <Flame className="h-5 w-5 text-anyskill-orange" />
            <span className="text-sm font-medium text-anyskill-purple">Our Vision</span>
          </div>
          <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-4">
            The <span className="text-anyskill-purple">AnySkill</span> Manifesto
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            A declaration of our commitment to building a new economy—one that empowers students and strengthens communities.
          </p>
        </motion.div>

        {/* Manifesto content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative bg-card/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 lg:p-16 shadow-xl border border-border/50">
            {/* Decorative corner elements */}
            <div className="absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-anyskill-purple/30 rounded-tl-lg" />
            <div className="absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-anyskill-purple/30 rounded-tr-lg" />
            <div className="absolute bottom-4 left-4 w-12 h-12 border-l-2 border-b-2 border-anyskill-purple/30 rounded-bl-lg" />
            <div className="absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-anyskill-purple/30 rounded-br-lg" />

            <div className="space-y-8 font-inter text-lg md:text-xl leading-relaxed text-foreground/90">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-2xl md:text-3xl font-poppins font-semibold text-foreground"
              >
                The old way of working is broken.
              </motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                For students, it means trading valuable years for minimum wage at apathetic corporations that don't care about their exams, their passions, or their future. For professionals, it means outsourcing talent to an anonymous face thousands of miles away, turning human skill into a commodity.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="py-6 px-8 bg-gradient-to-r from-anyskill-purple/10 via-anyskill-blue/10 to-anyskill-orange/10 rounded-2xl border border-anyskill-purple/20"
              >
                <p className="text-2xl md:text-3xl font-poppins font-bold text-center bg-gradient-to-r from-anyskill-purple via-anyskill-blue to-anyskill-orange bg-clip-text text-transparent">
                  AnySkill is the rejection of the transactional economy.
                </p>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                We believe that the best investment a professional can make is in their own backyard. We believe that a high school student shouldn't just be an 'employee'—they should be a founder of their own time, building a reputation before they even graduate.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="grid md:grid-cols-2 gap-6 py-4"
              >
                <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                  <div className="p-2 bg-anyskill-purple/10 rounded-lg">
                    <Users className="h-6 w-6 text-anyskill-purple" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold mb-1">When you hire on AnySkill</h3>
                    <p className="text-base text-muted-foreground">You aren't just clearing your to-do list. You are funding a student's ambition. You are keeping capital in your local economy. You are mentoring the next generation of leaders.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-background/50 rounded-xl">
                  <div className="p-2 bg-anyskill-blue/10 rounded-lg">
                    <Rocket className="h-6 w-6 text-anyskill-blue" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold mb-1">When you work on AnySkill</h3>
                    <p className="text-base text-muted-foreground">You aren't just earning cash. You are building a portfolio, learning the grit of entrepreneurship, and serving the neighbors who will become your future network.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="text-center pt-8"
              >
                <p className="text-xl md:text-2xl font-poppins font-medium text-muted-foreground mb-4">
                  We are not a gig economy.
                </p>
                <p className="text-3xl md:text-4xl font-poppins font-bold bg-gradient-to-r from-anyskill-purple via-anyskill-blue to-anyskill-orange bg-clip-text text-transparent">
                  We are the Launchpad Economy.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="text-center mt-16"
        >
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Link to="/discover-services">
              <Button size="lg" className="bg-anyskill-purple hover:bg-anyskill-purple/90 text-white px-8">
                <Heart className="mr-2 h-5 w-5" />
                Find Local Talent
              </Button>
            </Link>
            <Link to="/offer-service">
              <Button size="lg" variant="outline" className="border-anyskill-blue text-anyskill-blue hover:bg-anyskill-blue/10 px-8">
                <MapPin className="mr-2 h-5 w-5" />
                Offer Your Skills
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Manifesto;
